<template>
  <div class="delivery-new">
    <v-container>
      <v-form v-model="formValid" :ref="form">
        <v-autocomplete
          v-model="kunde"
          :items="kundenItems"
          item-title="text"
          label="Kunde"
          :rules="[ruleRequired]"
          v-model:search="kundenSearchWord"
          :loading="kundenLoading"
          :return-object="true"
          required
        />
        <date-picker
          class="mt-5"
          v-model="Lieferdatum"
          label="Lieferdatum"
          :rules="[ruleRequired]"
        />
        <v-btn color="primary" @click="save" :disabled="!formValid">Erstellen</v-btn>
      </v-form>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import {ProviderStatus} from '@/model/Provider';
import Provider from '@/model/Provider';
import router from '@/router';
import DatePicker from '@/components/DatePicker.vue';
import moment from 'moment';
import {doAlert} from '@/util';
import {getProviders} from '@/api/module/provider';
import {ValueType} from '@/api/meta/filters';
import {VNodeRef} from 'vue';

const kunde = ref<{ value: string; text: string, name: string } | null>(null);
const Lieferdatum = ref(moment().format('YYYY-MM-DD'));
const formValid = ref(false);
const kundenLoaded = ref(false);
const kunden = ref<Provider[]>([]);
const debounceKundenTimeout = ref<NodeJS.Timeout | null>(null);
const kundenSearchWord = ref('');
const kundenLoading = ref(false);
const form = ref<HTMLFormElement & VNodeRef | undefined>();

const kundenItems = computed(() => {
  return kunden.value.map((l) => ({
    value: String(l.KundenNr),
    text: `${l.Kundname} [${l.KundenNr}]`,
    name: l.Kundname,
  }));
});

function ruleRequired(val: string | null | undefined): string | true {
  if (!val) return 'erforderlich';

  return true;
}

async function loadKunden() {
  if (!kundenSearchWord.value) {
    kundenLoading.value = false;
    return;
  }
  try {
    [kunden.value] = await getProviders({
      KundStatus$In: [ProviderStatus.Kunden],
      Fulltext$Fulltext: {
        words: [String(kundenSearchWord.value)],
        fields: [
          {path: ['KundenNr'], valueType: ValueType.Naked},
          {path: ['Kundname'], valueType: ValueType.StringContains},
        ],
      },
    }, []);
  } finally {
    kundenLoading.value = false;
  }
}

async function save() {
  try {
    form.value?.validate();
    if (!formValid.value) throw new Error('Bitte alle Felder korrekt ausfüllen');

    router.push({path: '/picklist/-1', query: {kunde: kunde.value!.value, liferdatum: Lieferdatum.value}}).catch(console.error);
  } catch (err: any) {
    await doAlert(err.message, 'Fehler');
  }
}

watch(kundenSearchWord, () => {
  kundenLoading.value = true;

  if (debounceKundenTimeout.value) clearTimeout(debounceKundenTimeout.value);
  debounceKundenTimeout.value = setTimeout(loadKunden, 500);
});

watch(form, () => {
  form.value?.validate();
});

defineExpose({
  Error, Loading, DatePicker,
  kunde,
  Lieferdatum,
  formValid,
  kundenLoaded,
  kunden,
  debounceKundenTimeout,
  kundenSearchWord,
  kundenLoading,
  form,
  save,
  ruleRequired,
});

</script>

<style>
.item-operator-info .v-chip__content {
  display: flex;
  justify-content: center;
}

.v-pagination__navigation, .v-pagination__item--active {
  box-shadow: none;
}
</style>
